import { createTheme, type Direction, type Theme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { useEffect, useMemo } from 'react';

import components from './Components';
import typography from './Typography';
import { shadows, darkshadows } from './Shadows';
import { baseDarkTheme, baselightTheme } from './DefaultColorsV2';
import { RootState, useAppSelector } from '@shared/store';
import './expanded-theme';

export type CreateAppThemeConfig = {
  mode?: 'light' | 'dark';
  direction?: Direction;
  borderRadius?: number;
};

export const createAppTheme = (config: CreateAppThemeConfig = {}): Theme => {
  const mode = config.mode ?? 'light';
  const direction = config.direction ?? 'ltr';
  const borderRadius = config.borderRadius ?? 7;
  const defaultTheme = mode === 'dark' ? baseDarkTheme : baselightTheme;
  const defaultShadow = mode === 'dark' ? darkshadows : shadows;

  const theme = createTheme(
    deepmerge(defaultTheme, {
      palette: { mode },
      shape: { borderRadius },
      shadows: defaultShadow,
      typography,
      direction,
    }),
  );

  theme.components = components(theme) as Theme['components'];
  return theme;
};

export const useAppTheme = (): Theme => {
  const activeDir = useAppSelector((state: RootState) => state.customizer.activeDir);
  const activeMode = useAppSelector((state: RootState) => state.customizer.activeMode);
  const borderRadius = useAppSelector((state: RootState) => state.customizer.borderRadius);

  useEffect(() => {
    if (activeDir) {
      document.dir = activeDir;
    }
  }, [activeDir]);

  return useMemo(
    () =>
      createAppTheme({
        mode: activeMode,
        direction: activeDir,
        borderRadius,
      }),
    [activeDir, activeMode, borderRadius],
  );
};

/** @deprecated Use useAppTheme. Alias retained for existing imports. */
export const ThemeSettings = useAppTheme;
